import type { ModuleRegistryEntry, ModuleRouteDefinition, ModuleRoutingContext } from '@/app-router/types';
import { isPathOnlySettingUrl } from '@/utils/settings-validation';

const DEFAULT_SETTINGS_KEY_PREFIX = 'citizen_module';

export function getModuleSettingsKey(
  moduleId: string,
  suffix: string,
  { settingsKeyPrefix = DEFAULT_SETTINGS_KEY_PREFIX }: ModuleRoutingContext = {},
): string {
  return `${settingsKeyPrefix}_${moduleId}_${suffix}`;
}

export function getNestedModuleConfig(
  settings: Record<string, unknown>,
  moduleId: string,
  { settingsKeyPrefix = DEFAULT_SETTINGS_KEY_PREFIX }: ModuleRoutingContext = {},
): Record<string, unknown> | null {
  const nested = settings?.[`${settingsKeyPrefix}_${moduleId}`];
  return nested && typeof nested === 'object' ? (nested as Record<string, unknown>) : null;
}

export function isModuleRoutingEnabled(settings: Record<string, unknown>, moduleId: string, context: ModuleRoutingContext = {}): boolean {
  const enabledKey = getModuleSettingsKey(moduleId, 'enabled', context);

  if (enabledKey in settings) {
    const value = settings[enabledKey];
    return value !== false && value !== 'false' && value !== 0 && value !== '0';
  }

  const nested = getNestedModuleConfig(settings, moduleId, context);
  if (nested && 'enabled' in nested) {
    return nested.enabled !== false;
  }

  return true;
}

export function resolveModuleBasePath(
  settings: Record<string, unknown>,
  moduleId: string,
  defaultPath: string,
  context: ModuleRoutingContext = {},
): string {
  const pathKey = getModuleSettingsKey(moduleId, 'path', context);
  const configured = settings?.[pathKey];

  if (typeof configured === 'string' && isPathOnlySettingUrl(configured) && configured.startsWith('/')) {
    return configured.replace(/\/$/, '') || defaultPath;
  }

  if (configured != null && configured !== '') {
    console.warn(`[module-routes] Ignoring invalid path override "${pathKey}":`, configured);
  }

  return defaultPath;
}

export function resolveModuleRoutePath(
  settings: Record<string, unknown>,
  moduleId: string,
  route: ModuleRouteDefinition,
  context: ModuleRoutingContext = {},
): string {
  if (!route.pathSuffix) {
    return resolveModuleBasePath(settings, moduleId, route.defaultPath, context);
  }

  // Deeplink defaultPath often already includes the param segment (e.g. `/mina/arenden/:caseKey`
  // with pathSuffix `/:caseKey`). Strip it before resolving the base so we do not double-append.
  const defaultBase = route.defaultPath.endsWith(route.pathSuffix)
    ? route.defaultPath.slice(0, -route.pathSuffix.length) || '/'
    : route.defaultPath;

  const basePath = resolveModuleBasePath(settings, moduleId, defaultBase, context);
  return `${basePath}${route.pathSuffix}`;
}

function readSettingValue(settings: Record<string, unknown>, key: string): unknown {
  if (!(key in settings)) {
    return undefined;
  }

  const entry = settings[key];

  if (entry && typeof entry === 'object' && entry !== null && 'value' in entry) {
    return (entry as { value: unknown }).value;
  }

  return entry;
}

export function getModuleRoutingSettingsSnapshot(
  settings: Record<string, unknown>,
  registry: readonly ModuleRegistryEntry[],
  context: ModuleRoutingContext = {},
): Record<string, unknown> {
  const snapshot: Record<string, unknown> = {};

  for (const module of registry) {
    const enabledKey = getModuleSettingsKey(module.id, 'enabled', context);
    const pathKey = getModuleSettingsKey(module.id, 'path', context);
    const nestedKey = `${context.settingsKeyPrefix ?? DEFAULT_SETTINGS_KEY_PREFIX}_${module.id}`;

    if (enabledKey in settings) {
      snapshot[enabledKey] = readSettingValue(settings, enabledKey);
    }

    if (pathKey in settings) {
      snapshot[pathKey] = readSettingValue(settings, pathKey);
    }

    if (nestedKey in settings) {
      snapshot[nestedKey] = readSettingValue(settings, nestedKey);
    }
  }

  return snapshot;
}
